import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import api from '../services/api';
import { API } from '../App';
import { Card, CardHeader, CardTitle, CardDescription } from './ui/card';
import { 
  Truck, FileText, Car, Settings as SettingsIcon, TrendingUp, 
  ClipboardList, Package, Users, Briefcase, ExternalLink, AlertCircle,
  FolderOpen, FileQuestion, ChevronUp, ChevronDown, Plus, Upload, Phone, Wrench
} from 'lucide-react';
import { useBranch } from '../context/BranchContext';
import Quotes from '../pages/Quotes';
import AddCallToCrmModal from './AddCallToCrmModal';
import LogCallDropdownPill from './LogCallDropdownPill';
import EmbeddedTransportView from './EmbeddedTransportView';
import Proforma from '../pages/Proforma';

const enconOrange = '#ee7203';
const enconBlack = '#1d1d1b';
const enconGrey = '#9d9d9c';
const enconLightGrey = '#ededed';
const lightViolet = '#8b5cf6';

// Icon mapping
const iconMap = {
  'Truck': Truck,
  'FileText': FileText,
  'Car': Car,
  'Settings': SettingsIcon,
  'TrendingUp': TrendingUp,
  'ClipboardList': ClipboardList,
  'Package': Package,
  'Users': Users,
  'Briefcase': Briefcase,
  'ExternalLink': ExternalLink,
  'AlertCircle': AlertCircle,
};

// Dropdown Pill Component for Dashboard Quick Links
const DashboardDropdownPill = ({ label, icon: Icon, isQuote, user, onPanelClose }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openModal = (mode) => {
    setModalMode(mode);
    setShowDropdown(false);
    // Don't close panel - modal will render on top
  };

  const ModalComponent = isQuote ? Quotes : Proforma;

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowDropdown(!showDropdown);
          }}
          className={`flex items-center gap-2 px-5 py-2 rounded-full bg-white border-2 hover:shadow-lg transition-all duration-200 hover:scale-105 ${showDropdown ? 'shadow-lg' : ''}`}
          style={{ borderColor: enconOrange }}
        >
          <Icon className="w-4 h-4" style={{ color: enconOrange }} />
          <span className="font-semibold text-sm" style={{ color: enconOrange }}>{label}</span>
          <ChevronDown 
            className={`w-3 h-3 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} 
            style={{ color: enconOrange }} 
          />
        </button>

        {showDropdown && (
          <div 
            className="absolute left-1/2 -translate-x-1/2 mt-1 w-44 rounded-2xl shadow-xl overflow-hidden z-[100]"
            style={{
              background: 'linear-gradient(180deg, rgba(45, 45, 45, 0.98) 0%, rgba(30, 30, 30, 0.98) 100%)',
              border: `2px solid ${enconOrange}`,
              backdropFilter: 'blur(10px)',
              top: '100%'
            }}
          >
            <div 
              className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45"
              style={{ 
                background: 'rgba(45, 45, 45, 0.98)',
                borderLeft: `2px solid ${enconOrange}`,
                borderTop: `2px solid ${enconOrange}`
              }}
            />
            <div className="relative">
              <button
                onClick={() => openModal('create')}
                className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors rounded-t-xl"
              >
                <Plus className="w-4 h-4" style={{ color: enconOrange }} />
                <span className="font-medium text-sm">Create New</span>
              </button>
              <button
                onClick={() => openModal('upload')}
                className="w-full px-4 py-3 text-left text-white hover:bg-white/15 flex items-center gap-3 transition-colors border-t border-white/10 rounded-b-xl"
              >
                <Upload className="w-4 h-4" style={{ color: enconOrange }} />
                <span className="font-medium text-sm">Upload PDF</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {modalMode !== null && (
        <ModalComponent.Modal 
          isOpen={true} 
          onClose={() => setModalMode(null)} 
          mode={modalMode}
          user={user}
        />
      )}
    </>
  );
};

const DashboardPanel = ({ isOpen, onClose, user }) => {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [quickLinks, setQuickLinks] = useState([]);
  const [linkGroups, setLinkGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Animation state — allows slide-up on close before unmount
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [panelVisible, setPanelVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      const t = setTimeout(() => setPanelVisible(true), 10);
      return () => clearTimeout(t);
    } else {
      setPanelVisible(false);
      const t = setTimeout(() => setShouldRender(false), 320);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Get branch context for filtering
  const { getEffectiveBranchId } = useBranch();
  const effectiveBranchId = getEffectiveBranchId();

  // Mobile dropdown states
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [linksDropdownOpen, setLinksDropdownOpen] = useState(false);
  const hasPermission = (permissionKey) => {
    if (!permissionKey) return true;
    if (user?.role === 'super_admin') return true;
    return user?.[permissionKey] || false;
  };

  // Check if user has permission for M4.5 pills (defaults to true if not explicitly false)
  const hasPillPermission = (permissionKey) => {
    if (user?.role === 'super_admin') return true;
    return user?.[permissionKey] !== false;
  };

  // Load apps and quick links from backend
  useEffect(() => {
    if (isOpen) {
      loadApps();
      loadQuickLinks();
      loadLinkGroups();
    }
  }, [isOpen, user, effectiveBranchId]);

  const loadApps = async () => {
    try {
      // Include branch parameter for filtering
      const url = effectiveBranchId 
        ? `${API}/hub-apps`
        : `${API}/hub-apps`;
      const response = await api.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const transformedApps = response.data.map(app => {
        const hasAccess = hasPermission(app.permission_key);
        return {
          id: app.id,
          title: app.name,
          description: app.description,
          icon: app.icon_url ? null : iconMap[app.icon_type],
          customLogo: app.icon_url,
          route: app.route,
          available: hasAccess,
          color: hasAccess ? app.color : enconGrey,
          isExternalLink: app.is_external,
          externalUrl: app.is_external ? app.route : null,
        };
      });
      
      setApps(transformedApps);
    } catch (error) {
      console.error('Error loading hub apps:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadQuickLinks = async () => {
    try {
      // Include branch parameter for filtering
      const url = effectiveBranchId 
        ? `${API}/quick-links`
        : `${API}/quick-links`;
      const response = await api.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setQuickLinks(response.data);
    } catch (error) {
      console.error('Error loading quick links:', error);
    }
  };

  const loadLinkGroups = async () => {
    try {
      // Include branch parameter for filtering
      const url = effectiveBranchId 
        ? `${API}/quick-link-groups`
        : `${API}/quick-link-groups`;
      const response = await api.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setLinkGroups(response.data);
    } catch (error) {
      console.error('Error loading link groups:', error);
    }
  };

  const getLinksForGroup = (groupId) => {
    return quickLinks.filter(link => (link.group_id || 'default') === groupId).sort((a, b) => a.order - b.order);
  };

  const handleQuickLinkClick = (link) => {
    if (link.url.startsWith('http')) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(link.url);
      onClose();
    }
  };

  const handleAppClick = (app) => {
    if (!app.available) return;
    
    if (app.isExternalLink) {
      window.open(app.externalUrl, '_blank', 'noopener,noreferrer');
    } else {
      if (app.id === 'transport') {
        sessionStorage.setItem('transport_selected_date', new Date().toISOString());
      }
      navigate(app.route);
      onClose();
    }
  };

  if (!shouldRender) return null;

  // Header height: white nav row (py-3.5 + h-10 ≈ 68px) + transport bar (py-2 ≈ 36px) = ~104px.
  // Add 14px clearance so the panel starts visibly below the glass-tray rounded edge + shadow.
  const desktopTop = '118px';
  const mobileTop = '60px';

  // Shared panel transition style — slides in from slightly above, fades out smoothly
  const panelStyle = (top) => ({
    top,
    bottom: 0,
    borderBottom: `3px solid ${enconOrange}`,
    transition: 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.28s ease',
    transform: panelVisible ? 'translateY(0)' : 'translateY(-12px)',
    opacity: panelVisible ? 1 : 0,
    pointerEvents: panelVisible ? 'auto' : 'none',
  });

  return (
    <>
      {/* Backdrop - Desktop */}
      <div 
        className="hidden md:block fixed inset-0 bg-black/30 z-[48]"
        style={{
          top: desktopTop,
          transition: 'opacity 0.28s ease',
          opacity: panelVisible ? 1 : 0,
          pointerEvents: panelVisible ? 'auto' : 'none',
        }}
        onClick={onClose}
      />
      
      {/* Backdrop - Mobile */}
      <div 
        className="md:hidden fixed inset-0 bg-black/30 z-[48]"
        style={{
          top: mobileTop,
          transition: 'opacity 0.28s ease',
          opacity: panelVisible ? 1 : 0,
          pointerEvents: panelVisible ? 'auto' : 'none',
        }}
        onClick={onClose}
      />
      
      {/* Slide-down Panel - Desktop */}
      <div 
        className="hidden md:block fixed left-0 right-0 z-[49] bg-white shadow-2xl overflow-hidden"
        style={panelStyle(desktopTop)}
      >
        <div className="overflow-y-auto h-full pt-7" style={{ paddingBottom: '88px' }}>
          {/* Embedded Transport view fills the full dropdown — links and pills removed */}
          <EmbeddedTransportView
            user={user}
            onNavigate={(date) => { navigate(`/transport?date=${format(date, 'yyyy-MM-dd')}`); onClose(); }}
          />
        </div>
      </div>
      
      {/* Slide-down Panel - Mobile */}
      <div 
        className="md:hidden fixed left-0 right-0 z-[39] bg-white shadow-2xl overflow-hidden"
        style={panelStyle(mobileTop)}
      >
        <div className="overflow-y-auto h-full pt-7" style={{ paddingBottom: '88px' }}>
          {/* Embedded Transport view fills the full mobile panel — links and pills removed */}
          <EmbeddedTransportView
            user={user}
            onNavigate={(date) => { navigate(`/transport?date=${format(date, 'yyyy-MM-dd')}`); onClose(); }}
          />
        </div>
      </div>
      
      {/* Add Call to CRM Modal */}
      <AddCallToCrmModal isOpen={showCallModal} onClose={() => setShowCallModal(false)} />
    </>
  );
};

export default DashboardPanel;
